// RecorderStore: JS MobX wrapper around existing Recorder class
import { makeAutoObservable, runInAction } from "mobx";
import Recorder from "../components/Recorder";

export const RECORDER_STATES = {
  STOPPED: "Stopped",
  STARTING: "Starting",
  STARTED: "Recording in Progress",
  SAVING: "Saving Recording",
  SAVED: "Recording Saved",
};

// Backstop for auto-mute: the unmute depends on a realtime 'outputStopped' event that can be
// missed across reconnects. Section intros are <40s, so this only fires if that event is lost.
const AUTO_MUTE_MAX_MS = 55_000;

export class RecorderStore {
  constructor() {
    this.recorder = new Recorder(true);
    this.stream = null;
    this.recorderState = RECORDER_STATES.STOPPED;
    this.micLevel = 0;
    this.isMuted = false;
    this.micHealthy = true;
    this.cameraReady = false;
    // Auto-mute state: tracks whether mic is auto-muted during section intro
    this.isAutoMuted = false;
    // Tracks whether user manually muted before auto-mute started
    this._wasManuallyMutedBeforeAutoMute = false;
    // Safety timer that force-lifts auto-mute if the unmute event is never received
    this._autoMuteSafetyTimer = null;
    makeAutoObservable(this, { _wasManuallyMutedBeforeAutoMute: false, _autoMuteSafetyTimer: false }, { autoBind: true });
    this.recorder.on("cameraStatusChange", (ready) => {
      this.setCameraReady(ready);
    });
    this.recorder.on("micStatusChange", (ready) => {
      if (!ready) {
        runInAction(() => this.micHealthy = false);
      }
    });
  }

  async init() {
    if (this.stream) return;
    // Wait for StreamingMic/recorder to connect microphone externally and set stream
    // Here we simply expose hooks; the caller is expected to wire onMicConnected to set stream.
  }

  setStream(stream) {
    this.stream = stream;
  }

  setCameraReady(ready) { this.cameraReady = !!ready; }

  setMicLevel(level) {
    const newLevel = Math.max(0, level | 0);
    this.micLevel = newLevel > this.micLevel ? newLevel : this.micLevel; // peak hold-style
    const mutedByOS = !!this.stream?.getAudioTracks?.()[0]?.muted;
    this.micHealthy = this.micLevel > 2 && !mutedByOS;
  }

  async start() {
    if (this.recorderState !== RECORDER_STATES.STOPPED) return;
    runInAction(() => { this.recorderState = RECORDER_STATES.STARTING; });
    await this.recorder.start();
    runInAction(() => { this.recorderState = RECORDER_STATES.STARTED; });
  }

  async stop(saveMode = true) {
    if (this.recorderState !== RECORDER_STATES.STARTED && this.recorderState !== RECORDER_STATES.SAVING) return;
    if (saveMode) runInAction(() => { this.recorderState = RECORDER_STATES.SAVING; });
    await this.recorder.stop(true);
    runInAction(() => { this.recorderState = saveMode ? RECORDER_STATES.SAVED : RECORDER_STATES.STOPPED; });
  }

  mute() { this.recorder.mute(); this.isMuted = true; }
  unmute() { this.recorder.unmute(); this.isMuted = false; }

  /**
   * Start auto-mute mode for section intro.
   * Records whether user was already muted before auto-mute.
   * Idempotent: if already in auto-mute mode, does nothing.
   */
  startAutoMute() {
    // If already in auto-mute mode, don't re-initialize (prevents double-call from corrupting state)
    if (this.isAutoMuted) return;
    this._wasManuallyMutedBeforeAutoMute = this.isMuted;
    if (!this.isMuted) {
      this.mute();
    }
    this.isAutoMuted = true;
    // Backstop: never leave the mic stuck if the unmute event is lost (see AUTO_MUTE_MAX_MS).
    if (this._autoMuteSafetyTimer) clearTimeout(this._autoMuteSafetyTimer);
    this._autoMuteSafetyTimer = setTimeout(() => this.endAutoMute(), AUTO_MUTE_MAX_MS);
  }

  /**
   * End auto-mute mode when assistant stops speaking.
   * Only unmutes if user wasn't manually muted before.
   */
  endAutoMute() {
    console.log('endAutoMute called', "isAutoMuted:", this.isAutoMuted, "wasManuallyMutedBeforeAutoMute:", this._wasManuallyMutedBeforeAutoMute);
    if (this._autoMuteSafetyTimer) { clearTimeout(this._autoMuteSafetyTimer); this._autoMuteSafetyTimer = null; }
    if (!this.isAutoMuted) return;
    this.isAutoMuted = false;
    // Only unmute if user wasn't manually muted before auto-mute started
    if (!this._wasManuallyMutedBeforeAutoMute) {
      this.unmute();
    }
  }

  mixRemoteAudio(stream) { this.recorder.mixAudioStream(stream); }

  dispose() { if (this._autoMuteSafetyTimer) { clearTimeout(this._autoMuteSafetyTimer); this._autoMuteSafetyTimer = null; } try { this.recorder.stopTracks(); } catch (e) { void e; } this.stream = null; }
}
